import type { ComponentProps } from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import { cn } from "@/lib/utils"

// Primary is neutral ink: the ordinary main action of a view. `brand` is the
// accent, kept for the one action a workout screen is for (start, log the
// set, finish), at most one per screen. `outline` is the secondary action,
// `secondary` a filled quiet one (the steppers), `ghost` the tertiary one and
// every icon button.
//
// Sizes are for a thumb: `default` is 44px, `xl` 56px for the actions taken
// between sets; `sm` (36px) is for dense rows read with a pointer.
const buttonVariants = cva(
  "relative inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap select-none transition-[color,background-color,border-color,box-shadow,scale] ease-standard active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-[1.125rem]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        brand:
          "bg-brand font-semibold text-brand-foreground shadow-[inset_0_1px_0_rgb(255_255_255/0.18)] hover:bg-brand/90",
        outline:
          "border border-input bg-card text-foreground hover:border-border-strong hover:bg-accent",
        secondary: "bg-surface-2 text-foreground hover:bg-surface-3",
        ghost: "text-muted-foreground hover:bg-accent hover:text-foreground",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        link: "h-auto px-0 text-foreground underline underline-offset-4 decoration-border-strong hover:decoration-foreground active:scale-100",
      },
      size: {
        sm: "h-9 px-3",
        default: "h-11 px-4",
        lg: "h-12 px-5 text-base",
        xl: "h-14 rounded-lg px-6 text-base font-semibold [&_svg:not([class*='size-'])]:size-5",
        "icon-sm": "size-9",
        icon: "size-11",
        "icon-xl": "size-14 rounded-lg [&_svg:not([class*='size-'])]:size-6",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }

function Button({
  className,
  variant,
  size,
  asChild = false,
  type = "button",
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  return (
    <Comp
      data-slot="button"
      type={asChild ? undefined : type}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
